import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { TranslateModule } from '@ngx-translate/core';
import { FormatDatePipe } from '../../pipes/format-date.pipe';
import { ClientService } from '../../services/client-service';
import { firstValueFrom, lastValueFrom } from 'rxjs';

export interface ActiveTripData {
  scheduledTime?: string;
  initialAddress: string;
  destinationAddress: string;
  nbrPassengers: number;
  nbrLuggage: number;
  status?: string;
  fare?: number;
  ticket?: {
    driverId?: string;
    driverName?: string;
    driverRating?: number;
    driverImage?: string;
    driverPhone?: string;
  };
}

@Component({
  selector: 'app-active-trip-dialog',
  standalone: true,
  imports: [CommonModule, MatIconModule, MatButtonModule, TranslateModule, FormatDatePipe],
  templateUrl: './active-trip-dialog.component.html',
  styleUrls: ['./active-trip-dialog.component.scss']
})
export class ActiveTripDialogComponent implements OnInit{
  // Search status and offers data
  searchStatus: string = '';
  offersCount: number = 0;
  isSearching: boolean = false;

  constructor(
    public dialogRef: MatDialogRef<ActiveTripDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private clientService:ClientService
  ) {

    
  }
    async ngOnInit(): Promise<void> {
      //  alert('ActiveTripDialogComponent initialized with data: ' );
       console.log('ActiveTripData received in dialog:', this.data);
      
      try {
        // Get offers (drivers found)
        let res = await firstValueFrom(this.clientService.getOffersByTripId(this.data.activeTripId, 0));  
        this.offersCount = res?.data?.length || 0;
        console.log('Offers for trip:', res);
        
        // Get search status
        let resSearch = await lastValueFrom(this.clientService.getTicketSearch(this.data.activeTripId));  
        this.searchStatus = resSearch?.data?.status || '';
        this.isSearching = this.searchStatus === 'started';
        console.log('Search info for trip:', resSearch);
      } catch (error) {
        console.error('Error fetching trip data:', error);
      }

      this.clientService.cancelSearch$.subscribe((tripId:string)=>{
        // alert('Cancel search received for tripId: ' + tripId);
          this.isSearching = false;
          this.searchStatus = 'finished';
      });
      this.clientService.listDriversAccepted$.subscribe((drivers) => {
        // Faire quelque chose avec la liste des drivers
        this.offersCount = drivers.length;
        console.log('Updated offers count from accepted drivers:', this.offersCount);
      });
    }

  onKeepSearching(): void {
    this.dialogRef.close('keep_searching');
  }

  onContinueWithDriver(): void {
    this.dialogRef.close('keep_searching');
  }

  isHasDriver(): boolean {
    return this.data?.ticket?.driverId ? true : false;
  }

  /**
   * Get the appropriate message based on search status and offers count
   */
  getStatusMessage(): string {
    // If user has a driver
    if (this.isHasDriver()) {
      return 'ACTIVE_TRIP.YOU_ARE_WITH_DRIVER';
    }

    // If search is still ongoing
    if (this.isSearching) {
      // If drivers were found while still searching
      if (this.offersCount > 0) {
        return 'ACTIVE_TRIP.DRIVERS_FOUND_SEARCHING';
      }
      // If still searching with no drivers yet
      return 'ACTIVE_TRIP.SEARCH_IN_PROGRESS';
    }

    // If search is finished
    if (this.searchStatus === 'finished') {
      // If drivers were found
      if (this.offersCount > 0) {
        return 'ACTIVE_TRIP.DRIVERS_FOUND';
      }
      // If no drivers found - encourage increasing price
      else {
        return 'ACTIVE_TRIP.NO_DRIVERS_ENCOURAGE_PRICE';
      }
    }

    // Default message
    return 'ACTIVE_TRIP.YOU_ARE_IN_TRIP';
  }

  /**
   * Get the message parameter (number of drivers found)
   */
  getMessageParams(): any {
    return { count: this.offersCount };
  }

  /**
   * Get the text for the right button based on search status
   */
  getRightButtonText(): string {
    // If user has a driver
    if (this.isHasDriver()) {
      return 'ACTIVE_TRIP.CONTINUE_WITH_DRIVER';
    }

    // If search is finished with drivers found
    if (this.searchStatus === 'finished' && this.offersCount > 0) {
      return 'ACTIVE_TRIP.ACCEPT_DRIVER_BTN';
    }

    // If search is finished with no drivers
    if (this.searchStatus === 'finished' && this.offersCount === 0) {
      return 'ACTIVE_TRIP.ADJUST_PRICE_BTN';
    }

    // Default (searching or drivers found)
    return 'ACTIVE_TRIP.KEEP_SEARCHING';
  }

  /**
   * Handle the right button click based on current state
   */
  onRightButtonClick(): void {
    // If user has a driver
    if (this.isHasDriver()) {
      this.onContinueWithDriver();
      return;
    }

    // If search is finished with drivers found
    if (this.searchStatus === 'finished' && this.offersCount > 0) {
      this.dialogRef.close('keep_searching');
      return;
    }

    // If search is finished with no drivers
    if (this.searchStatus === 'finished' && this.offersCount === 0) {
      this.dialogRef.close('keep_searching');
      return;
    }

    // Default
    this.onKeepSearching();
  }

  onClose(): void {
    this.dialogRef.close('');
  }

  onCancel(): void {
    this.dialogRef.close('cancel');
  }
}
